/**
 * UI Testing Panel — right-side panel for UI/browser test execution
 * with three tabs: Live Execution, Dashboard, and Generated Code.
 *
 * Mirrors the tabbed pattern used in APIExecutionPanel but adapted
 * for browser-based UI testing workflows.
 */
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  LinearProgress,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Button,
  Paper,
  TextField,
} from '@mui/material';
import {
  CheckCircle as PassedIcon,
  Cancel as FailedIcon,
  HourglassEmpty as RunningIcon,
  SkipNext as SkippedIcon,
  OndemandVideo as LiveIcon,
  Assessment as DashboardIcon,
  Code as CodeIcon,
  Stop as StopIcon,
  Cancel as CancelIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Speed as SpeedIcon,
  OpenInBrowser as BrowserIcon,
  Link as LinkIcon,
  VideoLibrary as VideoIcon,
  VpnKey as CredentialIcon,
} from '@mui/icons-material';
import {
  TestingSessionPhase, TestingStepResult, TestingSessionStep, GeneratedCodePayload,
  PageTestResult, TestCaseIdentified, SessionHistorySummary, TestingAction, ExistingCodeInfo,
} from '../types';
import { QualityAssessment } from '../types';
import GeneratedCodeViewer from './GeneratedCodeViewer';
import LiveKitViewer from './LiveKitViewer';
import SessionHistoryCard from './SessionHistoryCard';
import CodeDiffViewer from './CodeDiffViewer';
import LiveViewFrame from './LiveViewFrame';
import CodeRepositoryTab from './CodeRepositoryTab';

// ============================================================================
// Types & Helpers
// ============================================================================

const PHASE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
  initializing: 'default',
  planning: 'info',
  provisioning: 'info',
  gathering_context: 'info',
  awaiting_credentials: 'warning',
  awaiting_captcha: 'warning',
  crawling: 'info',
  navigating: 'primary',
  analyzing_dom: 'info',
  generating_steps: 'info',
  awaiting_approval: 'warning',
  executing: 'primary',
  review_failures: 'warning',
  retrying: 'secondary',
  analyzing: 'info',
  exploring: 'info',
  generating_code: 'info',
  creating_tms_entities: 'info',
  interactive: 'success',
  page_testing: 'primary',
  page_code_generation: 'info',
  page_execution: 'primary',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
};

const formatDuration = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const formatMs = (ms?: number): string => {
  if (ms == null) return '--';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

type StepStatus = 'passed' | 'failed' | 'running' | 'skipped' | 'pending';

const STATUS_CONFIG: Record<StepStatus, { label: string; color: string; icon: React.ReactNode }> = {
  passed: { label: 'Passed', color: '#2e7d32', icon: <PassedIcon sx={{ fontSize: '0.85rem', color: '#2e7d32' }} /> },
  failed: { label: 'Failed', color: '#c62828', icon: <FailedIcon sx={{ fontSize: '0.85rem', color: '#c62828' }} /> },
  running: { label: 'Running', color: '#1565c0', icon: <RunningIcon sx={{ fontSize: '0.85rem', color: '#1565c0', animation: 'spin 1.5s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} /> },
  skipped: { label: 'Skipped', color: '#757575', icon: <SkippedIcon sx={{ fontSize: '0.85rem', color: '#757575' }} /> },
  pending: { label: 'Pending', color: '#9e9e9e', icon: <RunningIcon sx={{ fontSize: '0.85rem', color: '#9e9e9e' }} /> },
};

// ============================================================================
// Props
// ============================================================================

interface UITestingPanelProps {
  projectId?: string | number | null;
  panelFramework?: string | null;
  /** Force the interactive VNC (not one-way LiveKit) — e.g. human solving a CAPTCHA. */
  forceInteractiveView?: boolean;
  liveStreamUrl?: string | null;
  vncUrl?: string | null;
  livekitToken?: string | null;
  livekitUrl?: string | null;
  livekitRoom?: string | null;
  phase: TestingSessionPhase;
  duration: number;
  url: string;
  steps: TestingSessionStep[];
  stepResults: TestingStepResult[];
  generatedCode: { files: any[]; summary: string; framework?: string; tms_test_case_id?: string } | null;
  summary: {
    total_steps: number;
    passed: number;
    failed: number;
    video_url?: string;
    tms_entities?: any[];
  } | null;
  qualityAssessment: QualityAssessment | null;
  // Per-page iterative testing props
  currentPageIndex?: number | null;
  totalPages?: number | null;
  currentPageTitle?: string | null;
  pageResults?: PageTestResult[];
  // Per-page HITL review props
  isTestCaseReviewPending?: boolean;
  currentPageTestCases?: TestCaseIdentified[];
  isCodeReviewPending?: boolean;
  currentPageCode?: { file_name: string; code: string } | null;
  onApproveTestCases?: (modifiedTestCases?: TestCaseIdentified[]) => void;
  onApproveCode?: (modifiedCode?: string) => void;
  isCredentialRequested?: boolean;
  loginFields?: { type: string; selector: string; label: string }[];
  onSubmitCredentials?: (username: string, password: string) => void;
  onSkipLogin?: () => void;
  // Returning user props (Phase 2 + 3)
  sessionHistory?: SessionHistorySummary | null;
  isActionRequired?: boolean;
  availableActions?: TestingAction[];
  existingCodeInfo?: ExistingCodeInfo[];
  onSelectAction?: (action: string) => void;
  onEndSession: () => void;
  onCancel: () => void;
}

// ============================================================================
// StatCard — reusable stat display
// ============================================================================

const StatCard: React.FC<{
  label: string; value: number; color: string; icon: React.ReactNode;
  active?: boolean; onClick?: () => void;
}> = ({ label, value, color, icon, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: 1, minWidth: 70, p: 0.75, borderRadius: 1,
      border: '2px solid', textAlign: 'center',
      borderColor: active ? color : 'divider',
      bgcolor: active ? `${color}08` : undefined,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.15s ease',
      '&:hover': onClick ? { borderColor: color, bgcolor: `${color}08` } : {},
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.25 }}>
      {icon}
      <Typography variant="h6" fontWeight={700} fontSize="1.1rem" sx={{ color }}>
        {value}
      </Typography>
    </Box>
    <Typography variant="caption" color="text.secondary" fontSize="0.6rem" display="block">
      {label}
    </Typography>
  </Box>
);

// ============================================================================
// Dashboard Tab
// ============================================================================

type StatusFilter = 'all' | StepStatus;

const DashboardView: React.FC<{
  steps: TestingSessionStep[];
  stepResults: TestingStepResult[];
  phase: TestingSessionPhase;
  duration: number;
  summary: UITestingPanelProps['summary'];
  qualityAssessment: QualityAssessment | null;
  currentPageIndex?: number | null;
  totalPages?: number | null;
  currentPageTitle?: string | null;
  pageResults?: PageTestResult[];
  sessionHistory?: SessionHistorySummary | null;
}> = ({ steps, stepResults, phase, duration, summary, qualityAssessment, currentPageIndex, totalPages, currentPageTitle, pageResults, sessionHistory }) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [copied, setCopied] = useState(false);

  const stats = useMemo(() => {
    const total = steps.length;
    const resultMap = new Map(stepResults.map(r => [r.step_index, r]));

    let passed = 0, failed = 0, running = 0, skipped = 0, pending = 0;
    const mergedSteps: Array<{
      index: number;
      description: string;
      action_id: string;
      status: StepStatus;
      duration_ms?: number;
      error?: string;
      healed?: boolean;
      healingStrategy?: string;
    }> = [];

    steps.forEach((step, i) => {
      const result = resultMap.get(i) || resultMap.get(step.index);
      let status: StepStatus = 'pending';
      if (result) {
        status = (result.status as StepStatus) || 'pending';
      } else if (step.status === 'running') {
        status = 'running';
      } else if (step.status === 'passed') {
        status = 'passed';
      } else if (step.status === 'failed') {
        status = 'failed';
      }

      switch (status) {
        case 'passed': passed++; break;
        case 'failed': failed++; break;
        case 'running': running++; break;
        case 'skipped': skipped++; break;
        default: pending++; break;
      }

      mergedSteps.push({
        index: i,
        description: step.description || step.action_id || `Step ${i + 1}`,
        action_id: step.action_id,
        status,
        duration_ms: result?.duration_ms,
        error: result?.error,
        healed: result?.healed,
        healingStrategy: result?.healing_strategy,
      });
    });

    // If we have summary data from the completed event, prefer those counts
    if (summary) {
      passed = summary.passed || passed;
      failed = summary.failed || failed;
    }

    // Performance stats
    const durations = stepResults
      .map(r => r.duration_ms)
      .filter((d): d is number => d != null && d > 0);
    const avgDuration = durations.length > 0
      ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
      : 0;

    return { total, passed, failed, running, skipped, pending, mergedSteps, avgDuration };
  }, [steps, stepResults, summary]);

  const filteredSteps = useMemo(() => {
    if (statusFilter === 'all') return stats.mergedSteps;
    return stats.mergedSteps.filter(s => s.status === statusFilter);
  }, [stats.mergedSteps, statusFilter]);

  const completed = stats.passed + stats.failed + stats.skipped;
  const progress = stats.total > 0 ? (completed / stats.total) * 100 : 0;
  const passRate = completed > 0 ? Math.round((stats.passed / completed) * 100) : 0;

  const handleCopy = () => {
    const header = '#\tStep\tStatus\tDuration\tError';
    const lines = filteredSteps.map((s, i) => {
      return `${i + 1}\t${s.description}\t${s.status}\t${formatMs(s.duration_ms)}\t${s.error || '--'}`;
    });
    navigator.clipboard.writeText([header, ...lines].join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box sx={{ p: 1.5, overflow: 'auto', height: '100%' }}>
      {/* Overview cards */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
        <StatCard label="Total" value={stats.total} color="#424242"
          icon={<BrowserIcon sx={{ fontSize: '0.85rem', color: '#424242' }} />}
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')} />
        <StatCard label="Passed" value={stats.passed} color="#2e7d32"
          icon={<PassedIcon sx={{ fontSize: '0.85rem', color: '#2e7d32' }} />}
          active={statusFilter === 'passed'}
          onClick={() => setStatusFilter(statusFilter === 'passed' ? 'all' : 'passed')} />
        <StatCard label="Failed" value={stats.failed} color="#c62828"
          icon={<FailedIcon sx={{ fontSize: '0.85rem', color: '#c62828' }} />}
          active={statusFilter === 'failed'}
          onClick={() => setStatusFilter(statusFilter === 'failed' ? 'all' : 'failed')} />
        {stats.skipped > 0 && (
          <StatCard label="Skipped" value={stats.skipped} color="#757575"
            icon={<SkippedIcon sx={{ fontSize: '0.85rem', color: '#757575' }} />}
            active={statusFilter === 'skipped'}
            onClick={() => setStatusFilter(statusFilter === 'skipped' ? 'all' : 'skipped')} />
        )}
        <StatCard label="Running" value={stats.running + stats.pending} color="#1565c0"
          icon={<RunningIcon sx={{ fontSize: '0.85rem', color: '#1565c0' }} />}
          active={statusFilter === 'running'}
          onClick={() => setStatusFilter(statusFilter === 'running' ? 'all' : 'running')} />
      </Box>

      {/* Progress bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ flex: 1, height: 6, borderRadius: 3 }}
          color={stats.failed > 0 ? 'error' : 'success'}
        />
        <Typography variant="caption" color="text.secondary" fontSize="0.7rem" fontWeight={600}>
          {completed}/{stats.total}
        </Typography>
      </Box>

      {/* Pass rate & performance */}
      <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        {completed > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" fontSize="0.7rem" color="text.secondary">
              Pass Rate:
            </Typography>
            <Typography variant="caption" fontSize="0.75rem" fontWeight={700}
              sx={{ color: passRate >= 80 ? '#2e7d32' : passRate >= 50 ? '#ed6c02' : '#c62828' }}>
              {passRate}%
            </Typography>
          </Box>
        )}
        {stats.avgDuration > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SpeedIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
            <Typography variant="caption" fontSize="0.7rem" color="text.secondary">
              Avg: <strong>{formatMs(stats.avgDuration)}</strong>
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" fontSize="0.7rem" color="text.secondary">
            Duration:
          </Typography>
          <Typography variant="caption" fontSize="0.7rem" fontFamily="monospace" fontWeight={600}>
            {formatDuration(duration)}
          </Typography>
        </Box>
      </Box>

      {/* Quality assessment badge */}
      {qualityAssessment && (
        <Box sx={{
          mb: 1.5, p: 1, borderRadius: 1,
          border: '1px solid',
          borderColor: qualityAssessment.grade === 'A' ? 'success.main'
            : qualityAssessment.grade === 'B' ? 'info.main'
            : qualityAssessment.grade === 'C' ? 'warning.main' : 'error.main',
          bgcolor: qualityAssessment.grade === 'A' ? 'success.50'
            : qualityAssessment.grade === 'B' ? 'info.50'
            : qualityAssessment.grade === 'C' ? 'warning.50' : 'error.50',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} fontSize="0.8rem">
              Quality Grade: {qualityAssessment.grade}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {qualityAssessment.pass_rate}% pass rate
            </Typography>
          </Box>
          {qualityAssessment.flaky_count != null && qualityAssessment.flaky_count > 0 && (
            <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
              {qualityAssessment.flaky_count} flaky test(s) detected
            </Typography>
          )}
        </Box>
      )}

      {/* Video URL */}
      {summary?.video_url && (
        <Box sx={{ mb: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VideoIcon />}
            onClick={() => window.open(summary.video_url, '_blank')}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Watch Recording
          </Button>
        </Box>
      )}

      {/* TMS entities */}
      {summary?.tms_entities && summary.tms_entities.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" fontWeight={700} fontSize="0.65rem" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            TMS Entities Created
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {summary.tms_entities.map((entity, i) => (
              <Chip key={i} label={`${entity.type || 'entity'}: ${entity.name || entity.id || i + 1}`}
                size="small" variant="outlined" color="primary"
                icon={<LinkIcon sx={{ fontSize: '0.7rem' }} />}
                sx={{ height: 20, fontSize: '0.6rem' }} />
            ))}
          </Box>
        </Box>
      )}

      {/* Per-page progress indicator */}
      {totalPages != null && totalPages > 0 && (
        <Box sx={{
          mb: 1.5, p: 1, borderRadius: 1,
          border: '1px solid', borderColor: 'primary.200', bgcolor: 'primary.50',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="caption" fontWeight={700} fontSize="0.75rem" color="primary.main">
              Page {(currentPageIndex ?? 0) + 1} / {totalPages}
            </Typography>
            {currentPageTitle && (
              <Typography variant="caption" fontSize="0.7rem" color="text.secondary" sx={{
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {currentPageTitle}
              </Typography>
            )}
          </Box>
          <LinearProgress
            variant="determinate"
            value={((pageResults?.length ?? 0) / totalPages) * 100}
            sx={{ height: 4, borderRadius: 2 }}
            color="primary"
          />
          <Typography variant="caption" fontSize="0.6rem" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
            {pageResults?.length ?? 0} of {totalPages} pages completed
          </Typography>
        </Box>
      )}

      {/* Per-page results breakdown */}
      {pageResults && pageResults.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" fontWeight={700} fontSize="0.65rem" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Page Results
          </Typography>
          {pageResults.map((pr, i) => (
            <Box
              key={pr.node_id || i}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                p: 0.5, px: 1, mb: 0.25, borderRadius: 0.5,
                bgcolor: pr.failed > 0 ? 'error.50' : 'success.50',
                border: '1px solid',
                borderColor: pr.failed > 0 ? 'error.200' : 'success.200',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, overflow: 'hidden' }}>
                {pr.failed > 0
                  ? <FailedIcon sx={{ fontSize: '0.75rem', color: 'error.main' }} />
                  : <PassedIcon sx={{ fontSize: '0.75rem', color: 'success.main' }} />
                }
                <Typography variant="caption" fontSize="0.7rem" fontWeight={600} sx={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {pr.page_title || `Page ${i + 1}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                {/* History indicator (Phase 3) */}
                {(() => {
                  const pageDetail = sessionHistory?.page_details?.find(
                    (pd) => pd.url === pr.page_url || pd.page_title === pr.page_title
                  );
                  if (!pageDetail) {
                    return <Chip label="New" size="small" sx={{ height: 14, fontSize: '0.5rem', bgcolor: '#1976d220', color: '#1976d2' }} />;
                  }
                  if (pageDetail.status === 'passed') {
                    return <Chip label="Prev. Passed" size="small" sx={{ height: 14, fontSize: '0.5rem', bgcolor: '#2e7d3215', color: '#2e7d32' }} />;
                  }
                  return <Chip label="Prev. Failed" size="small" sx={{ height: 14, fontSize: '0.5rem', bgcolor: '#c6282815', color: '#c62828' }} />;
                })()}
                <Chip label={`${pr.passed}P`} size="small"
                  sx={{ height: 16, fontSize: '0.55rem', bgcolor: '#2e7d3220', color: '#2e7d32', fontWeight: 700 }} />
                {pr.failed > 0 && (
                  <Chip label={`${pr.failed}F`} size="small"
                    sx={{ height: 16, fontSize: '0.55rem', bgcolor: '#c6282820', color: '#c62828', fontWeight: 700 }} />
                )}
                <Typography variant="caption" fontSize="0.55rem" color="text.disabled" fontFamily="monospace">
                  {pr.code_file}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Step list header with copy */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, mb: 0.5 }}>
        <Typography variant="caption" fontWeight={700} fontSize="0.65rem" color="text.secondary">
          Test Steps ({filteredSteps.length}{filteredSteps.length !== stats.mergedSteps.length ? ` of ${stats.mergedSteps.length}` : ''})
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title={copied ? 'Copied!' : 'Copy list to clipboard'} arrow>
            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
              {copied
                ? <CheckIcon sx={{ fontSize: '0.85rem', color: 'success.main' }} />
                : <CopyIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Step table */}
      {stats.total === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No test steps yet — waiting for execution to begin.
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 400, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['#', 'Test Step', 'Status', 'Duration', 'Details'].map(h => (
                  <TableCell key={h} sx={{
                    py: 0.25, px: 0.75, fontSize: '0.6rem', fontWeight: 700,
                    bgcolor: 'grey.100', lineHeight: 1.4,
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSteps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      No steps match the selected filter
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSteps.map((step) => {
                  const cfg = STATUS_CONFIG[step.status] || STATUS_CONFIG.pending;
                  const isFailed = step.status === 'failed';
                  const isHealed = step.healed;

                  return (
                    <TableRow key={step.index} sx={{
                      bgcolor: isFailed ? 'error.50' : isHealed ? '#fff8e1' : undefined,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}>
                      <TableCell sx={{ py: 0.3, px: 0.75, fontSize: '0.65rem', color: 'text.secondary', width: 30 }}>
                        {step.index + 1}
                      </TableCell>
                      <TableCell sx={{
                        py: 0.3, px: 0.75, fontSize: '0.7rem',
                        maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }} title={step.description}>
                        {step.description}
                      </TableCell>
                      <TableCell sx={{ py: 0.3, px: 0.75 }}>
                        <Chip
                          label={cfg.label}
                          size="small"
                          icon={cfg.icon as React.ReactElement}
                          sx={{
                            height: 18, fontSize: '0.55rem', fontWeight: 600,
                            bgcolor: `${cfg.color}15`, color: cfg.color,
                            border: `1px solid ${cfg.color}40`,
                            '& .MuiChip-icon': { ml: 0.25 },
                          }}
                        />
                        {isHealed && (
                          <Chip label="healed" size="small" variant="filled"
                            sx={{ height: 14, fontSize: '0.5rem', ml: 0.5, bgcolor: '#ed6c02', color: '#fff' }} />
                        )}
                      </TableCell>
                      <TableCell sx={{
                        py: 0.3, px: 0.75, fontSize: '0.6rem', fontFamily: 'monospace',
                        color: 'text.secondary', width: 65,
                      }}>
                        {formatMs(step.duration_ms)}
                      </TableCell>
                      <TableCell sx={{
                        py: 0.3, px: 0.75, fontSize: '0.6rem', color: 'text.secondary',
                        maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }} title={step.error || (step.healingStrategy ? `Healed: ${step.healingStrategy}` : '')}>
                        {step.error || (step.healingStrategy ? `Healed: ${step.healingStrategy}` : '--')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ============================================================================
// Generated Code Tab
// ============================================================================

const GeneratedCodeTab: React.FC<{
  generatedCode: UITestingPanelProps['generatedCode'];
}> = ({ generatedCode }) => {
  if (!generatedCode || !generatedCode.files || generatedCode.files.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CodeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No generated code yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Code will appear here after test execution completes.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 1 }}>
      <GeneratedCodeViewer code={generatedCode as any} />
    </Box>
  );
};

// ============================================================================
// Main Panel
// ============================================================================

const UITestingPanel: React.FC<UITestingPanelProps> = ({
  projectId,
  panelFramework,
  forceInteractiveView,
  liveStreamUrl,
  vncUrl,
  livekitToken,
  livekitUrl,
  livekitRoom,
  phase,
  duration,
  url,
  steps,
  stepResults,
  generatedCode,
  summary,
  qualityAssessment,
  currentPageIndex,
  totalPages,
  currentPageTitle,
  pageResults,
  isTestCaseReviewPending,
  currentPageTestCases,
  isCodeReviewPending,
  currentPageCode,
  onApproveTestCases,
  onApproveCode,
  isCredentialRequested,
  loginFields,
  onSubmitCredentials,
  onSkipLogin,
  sessionHistory,
  isActionRequired,
  availableActions,
  existingCodeInfo,
  onSelectAction,
  onEndSession,
  onCancel,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [credUsername, setCredUsername] = useState('');
  const [credPassword, setCredPassword] = useState('');
  const [credSubmitted, setCredSubmitted] = useState(false);
  const [livekitFailed, setLivekitFailed] = useState(false);

  const isTerminal = phase === 'completed' || phase === 'failed' || phase === 'cancelled';

  // Compute quick stats for the header
  const passed = stepResults.filter(r => r.status === 'passed').length;
  const failed = stepResults.filter(r => r.status === 'failed').length;
  const running = stepResults.filter(r => r.status === 'running').length;
  const totalSteps = steps.length;
  const completed = passed + failed;
  const progress = totalSteps > 0 ? (completed / totalSteps) * 100 : 0;

  // Badge for code tab
  const hasCode = generatedCode && generatedCode.files && generatedCode.files.length > 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Header */}
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        {/* Title row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <BrowserIcon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
          <Typography variant="subtitle2" fontWeight={700} fontSize="0.85rem">
            {panelFramework && panelFramework !== 'selenium' && panelFramework !== 'playwright'
              ? 'API Test Execution'
              : 'UI Test Execution'}
          </Typography>
          <Chip
            label={phase.replace(/_/g, ' ')}
            size="small"
            color={PHASE_COLORS[phase] || 'default'}
            variant="outlined"
            sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize', fontWeight: 600 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              ml: 'auto',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 200,
            }}
            title={url}
          >
            {url}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {formatDuration(duration)}
          </Typography>
          {vncUrl && (
            <Tooltip title="Open VNC in new tab">
              <IconButton size="small" onClick={() => window.open(vncUrl, '_blank')}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Progress bar — only when there are steps */}
        {totalSteps > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ flex: 1, height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
              {completed}/{totalSteps}
            </Typography>
          </Box>
        )}

        {/* Stats row */}
        {totalSteps > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <PassedIcon sx={{ fontSize: '0.75rem', color: 'success.main' }} />
              <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color="success.main">
                {summary?.passed ?? passed}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <FailedIcon sx={{ fontSize: '0.75rem', color: 'error.main' }} />
              <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color="error.main">
                {summary?.failed ?? failed}
              </Typography>
            </Box>
            {running > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <RunningIcon sx={{ fontSize: '0.75rem', color: 'info.main' }} />
                <Typography variant="caption" fontWeight={600} fontSize="0.7rem" color="info.main">
                  {running}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: 30,
            '& .MuiTab-root': {
              minHeight: 30, fontSize: '0.7rem', py: 0,
              textTransform: 'none', minWidth: 'auto', px: 2,
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': { height: 2 },
          }}
        >
          <Tab
            label="Live Execution"
            icon={<LiveIcon sx={{ fontSize: '0.8rem' }} />}
            iconPosition="start"
            sx={{ gap: 0.5 }}
          />
          <Tab
            label="Dashboard"
            icon={<DashboardIcon sx={{ fontSize: '0.8rem' }} />}
            iconPosition="start"
            sx={{ gap: 0.5 }}
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Code Repository
                {hasCode && (
                  <Box sx={{
                    width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main',
                  }} />
                )}
              </Box>
            }
            icon={<CodeIcon sx={{ fontSize: '0.8rem' }} />}
            iconPosition="start"
            sx={{ gap: 0.5 }}
          />
        </Tabs>
      </Box>

      {/* Tab content */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {activeTab === 0 && (
          /* Live Execution — review cards, browser stream, or phase-aware placeholder */
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
              {/* Priority 0: Session History Card (returning user) */}
              {isActionRequired && sessionHistory && availableActions && availableActions.length > 0 ? (
                <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
                  <SessionHistoryCard
                    history={sessionHistory}
                    actions={availableActions}
                    onSelectAction={(action) => onSelectAction?.(action)}
                  />
                </Box>
              ) : null}

              {/* Priority 1: Test Case Review Card */}
              {!isActionRequired && isTestCaseReviewPending && currentPageTestCases && currentPageTestCases.length > 0 ? (
                <Box sx={{
                  height: '100%', overflow: 'auto', p: 2,
                  display: 'flex', flexDirection: 'column', gap: 2,
                }}>
                  <Paper elevation={0} sx={{
                    p: 2, border: '2px solid', borderColor: 'warning.main',
                    bgcolor: 'warning.50', borderRadius: 1.5,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <DashboardIcon fontSize="small" color="warning" />
                      <Typography variant="subtitle2" fontWeight={700} fontSize="0.9rem">
                        Review Test Cases
                      </Typography>
                      {totalPages != null && currentPageIndex != null && (
                        <Chip label={`Page ${currentPageIndex + 1}/${totalPages}`} size="small" color="primary" variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                      {currentPageTitle && (
                        <Typography variant="caption" color="text.secondary" sx={{
                          ml: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200,
                        }}>
                          {currentPageTitle}
                        </Typography>
                      )}
                    </Box>
                    <TableContainer sx={{ maxHeight: 300, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {['#', 'Test Case', 'Description', 'Priority', 'Category'].map(h => (
                              <TableCell key={h} sx={{
                                py: 0.25, px: 0.75, fontSize: '0.6rem', fontWeight: 700,
                                bgcolor: 'grey.100', lineHeight: 1.4,
                              }}>
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {currentPageTestCases.map((tc, i) => (
                            <TableRow key={i} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                              <TableCell sx={{ py: 0.3, px: 0.75, fontSize: '0.65rem', color: 'text.secondary', width: 30 }}>
                                {i + 1}
                              </TableCell>
                              <TableCell sx={{ py: 0.3, px: 0.75, fontSize: '0.7rem', fontWeight: 600, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                title={tc.name}>
                                {tc.name}
                              </TableCell>
                              <TableCell sx={{ py: 0.3, px: 0.75, fontSize: '0.65rem', color: 'text.secondary', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                title={tc.description}>
                                {tc.description}
                              </TableCell>
                              <TableCell sx={{ py: 0.3, px: 0.75 }}>
                                <Chip label={tc.priority} size="small" sx={{
                                  height: 18, fontSize: '0.55rem', fontWeight: 600,
                                  bgcolor: tc.priority === 'high' ? '#c6282815' : tc.priority === 'medium' ? '#ed6c0215' : '#2e7d3215',
                                  color: tc.priority === 'high' ? '#c62828' : tc.priority === 'medium' ? '#ed6c02' : '#2e7d32',
                                  border: `1px solid ${tc.priority === 'high' ? '#c6282840' : tc.priority === 'medium' ? '#ed6c0240' : '#2e7d3240'}`,
                                }} />
                              </TableCell>
                              <TableCell sx={{ py: 0.3, px: 0.75 }}>
                                <Chip label={tc.category} size="small" variant="outlined" sx={{
                                  height: 18, fontSize: '0.55rem',
                                }} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<PassedIcon />}
                        onClick={() => onApproveTestCases?.()}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Approve & Generate Code
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              ) : isCodeReviewPending && currentPageCode ? (
                /* Priority 2: Code Review Card */
                <Box sx={{
                  height: '100%', overflow: 'auto', p: 2,
                  display: 'flex', flexDirection: 'column', gap: 2,
                }}>
                  <Paper elevation={0} sx={{
                    p: 2, border: '2px solid', borderColor: 'info.main',
                    bgcolor: 'info.50', borderRadius: 1.5, flex: 1,
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CodeIcon fontSize="small" color="info" />
                      <Typography variant="subtitle2" fontWeight={700} fontSize="0.9rem">
                        Review Generated Code
                      </Typography>
                      {totalPages != null && currentPageIndex != null && (
                        <Chip label={`Page ${currentPageIndex + 1}/${totalPages}`} size="small" color="primary" variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }} />
                      )}
                      <Chip label={currentPageCode.file_name} size="small" variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem', ml: 'auto' }} />
                    </Box>
                    <Box sx={{
                      flex: 1, overflow: 'auto', bgcolor: '#1e1e1e', borderRadius: 1,
                      p: 1.5, mb: 1.5, fontFamily: 'monospace', fontSize: '0.7rem',
                      color: '#d4d4d4', whiteSpace: 'pre-wrap', lineHeight: 1.5,
                    }}>
                      {currentPageCode.code}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<PassedIcon />}
                        onClick={() => onApproveCode?.()}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Approve & Execute
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              ) : (livekitToken && livekitUrl && !livekitFailed && !forceInteractiveView) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ flex: totalSteps > 0 ? '0 0 55%' : 1, minHeight: 0, overflow: 'hidden' }}>
                    <LiveKitViewer
                      token={livekitToken}
                      livekitUrl={livekitUrl}
                      roomName={livekitRoom || undefined}
                      onFallback={() => setLivekitFailed(true)}
                      fallbackTimeoutSeconds={10}
                    />
                  </Box>
                  {/* Compact test results below LiveKit stream */}
                  {totalSteps > 0 && (
                    <Box sx={{
                      flex: 1, minHeight: 0, overflow: 'auto',
                      borderTop: '1px solid', borderColor: 'divider',
                    }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.5, py: 0.5, bgcolor: 'grey.50',
                        borderBottom: '1px solid', borderColor: 'divider',
                      }}>
                        <Typography variant="caption" fontWeight={700} sx={{ mr: 'auto' }}>
                          Test Results
                        </Typography>
                        <Chip size="small" label={`${passed} passed`} color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        <Chip size="small" label={`${failed} failed`} color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        {running > 0 && (
                          <Chip size="small" label={`${running} running`} color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                      <Box sx={{ px: 0.5 }}>
                        {steps.map((step: TestingSessionStep, i: number) => {
                          const result = stepResults.find(r => r.step_index === i);
                          const status = result?.status || step.status || 'pending';
                          return (
                            <Box
                              key={i}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                py: 0.25, px: 1,
                                borderBottom: '1px solid', borderColor: 'grey.100',
                                bgcolor: status === 'failed' ? 'error.50' : 'transparent',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                            >
                              {status === 'passed' ? (
                                <PassedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                              ) : status === 'failed' ? (
                                <FailedIcon sx={{ fontSize: 14, color: 'error.main' }} />
                              ) : status === 'running' ? (
                                <RunningIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                              ) : (
                                <SkippedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                              )}
                              <Typography variant="caption" sx={{
                                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap', fontSize: '0.7rem',
                              }}>
                                {step.description}
                              </Typography>
                              {result?.duration_ms != null && (
                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', flexShrink: 0 }}>
                                  {result.duration_ms < 1000 ? `${result.duration_ms}ms` : `${(result.duration_ms / 1000).toFixed(1)}s`}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (liveStreamUrl || vncUrl) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* VNC/Live Stream — shrink to make room for test results when available */}
                  <Box sx={{ flex: totalSteps > 0 ? '0 0 55%' : 1, minHeight: 0, overflow: 'hidden' }}>
                    <LiveViewFrame url={liveStreamUrl || vncUrl || ''} />
                  </Box>
                  {/* Compact test results below stream — shown when tests have run */}
                  {totalSteps > 0 && (
                    <Box sx={{
                      flex: 1, minHeight: 0, overflow: 'auto',
                      borderTop: '1px solid', borderColor: 'divider',
                    }}>
                      {/* Summary bar */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.5, py: 0.5, bgcolor: 'grey.50',
                        borderBottom: '1px solid', borderColor: 'divider',
                      }}>
                        <Typography variant="caption" fontWeight={700} sx={{ mr: 'auto' }}>
                          Test Results
                        </Typography>
                        <Chip size="small" label={`${passed} passed`} color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        <Chip size="small" label={`${failed} failed`} color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        {running > 0 && (
                          <Chip size="small" label={`${running} running`} color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                      {/* Compact step list */}
                      <Box sx={{ px: 0.5 }}>
                        {steps.map((step: TestingSessionStep, i: number) => {
                          const result = stepResults.find(r => r.step_index === i);
                          const status = result?.status || step.status || 'pending';
                          return (
                            <Box
                              key={i}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                py: 0.25, px: 1,
                                borderBottom: '1px solid', borderColor: 'grey.100',
                                bgcolor: status === 'failed' ? 'error.50' : 'transparent',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                            >
                              {status === 'passed' ? (
                                <PassedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                              ) : status === 'failed' ? (
                                <FailedIcon sx={{ fontSize: 14, color: 'error.main' }} />
                              ) : status === 'running' ? (
                                <RunningIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                              ) : (
                                <SkippedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                              )}
                              <Typography variant="caption" sx={{
                                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap', fontSize: '0.7rem',
                              }}>
                                {step.description}
                              </Typography>
                              {result?.duration_ms != null && (
                                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', flexShrink: 0 }}>
                                  {result.duration_ms < 1000 ? `${result.duration_ms}ms` : `${(result.duration_ms / 1000).toFixed(1)}s`}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                /* Pre-stream placeholder — phase-specific messaging */
                <Box sx={{
                  height: '100%', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 2, p: 3,
                }}>
                  {phase === 'planning' && (
                    <>
                      <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                        Planning test strategy...
                      </Typography>
                      <LinearProgress sx={{ width: '60%', borderRadius: 2 }} />
                      <Typography variant="caption" color="text.disabled">
                        Analyzing the target URL and generating a test plan
                      </Typography>
                    </>
                  )}
                  {phase === 'provisioning' && (
                    <>
                      <CircularProgress size={40} thickness={4} />
                      <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                        Provisioning browser session...
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Allocating a remote browser instance
                      </Typography>
                    </>
                  )}
                  {(phase === 'awaiting_credentials' || isCredentialRequested) && !credSubmitted && (
                    <>
                      <Paper elevation={0} sx={{
                        p: 2, width: '80%', maxWidth: 360,
                        border: '1px solid', borderColor: 'warning.300', bgcolor: 'warning.50', borderRadius: 1.5,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <CredentialIcon fontSize="small" color="warning" />
                          <Typography variant="body2" fontWeight={600}>
                            Login credentials required
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1.5 }}>
                          <TextField
                            size="small"
                            label={loginFields?.find(f => f.type === 'username')?.label || 'Username / Email'}
                            value={credUsername}
                            onChange={(e) => setCredUsername(e.target.value)}
                            autoFocus
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label={loginFields?.find(f => f.type === 'password')?.label || 'Password'}
                            type="password"
                            value={credPassword}
                            onChange={(e) => setCredPassword(e.target.value)}
                            fullWidth
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!credUsername}
                            onClick={() => {
                              onSubmitCredentials?.(credUsername, credPassword);
                              setCredSubmitted(true);
                            }}
                          >
                            Submit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={() => {
                              onSkipLogin?.();
                              setCredSubmitted(true);
                            }}
                          >
                            Skip Login
                          </Button>
                        </Box>
                      </Paper>
                      <Typography variant="caption" color="text.disabled">
                        Waiting for login credentials...
                      </Typography>
                    </>
                  )}
                  {phase === 'gathering_context' && (
                    <>
                      <CircularProgress size={36} thickness={4} color="info" />
                      <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                        Gathering application context...
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Analyzing page structure and content
                      </Typography>
                    </>
                  )}
                  {phase === 'crawling' && (
                    <>
                      <CircularProgress size={36} thickness={4} />
                      <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                        Connecting to browser...
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Establishing live stream connection
                      </Typography>
                    </>
                  )}
                  {(phase === 'page_testing' || phase === 'page_code_generation' || phase === 'page_execution') && (
                    <>
                      <CircularProgress size={36} thickness={4} />
                      <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                        {phase === 'page_testing'
                          ? `Analyzing page ${(currentPageIndex ?? 0) + 1}/${totalPages || '?'}...`
                          : phase === 'page_code_generation'
                          ? `Generating code for ${currentPageTitle || 'page'}...`
                          : `Executing tests for ${currentPageTitle || 'page'}...`
                        }
                      </Typography>
                      {totalPages != null && totalPages > 0 && (
                        <LinearProgress
                          variant="determinate"
                          value={((pageResults?.length ?? 0) / totalPages) * 100}
                          sx={{ width: '60%', borderRadius: 2, mt: 1 }}
                        />
                      )}
                      <Typography variant="caption" color="text.disabled">
                        {currentPageTitle || url}
                      </Typography>
                    </>
                  )}
                  {/* Default fallback for other pre-stream phases */}
                  {phase !== 'planning' && phase !== 'provisioning' && phase !== 'gathering_context' && phase !== 'crawling'
                    && phase !== 'page_testing' && phase !== 'page_code_generation' && phase !== 'page_execution'
                    && !(phase === 'awaiting_credentials' || isCredentialRequested) && (
                    <>
                      <CircularProgress size={36} thickness={4} />
                      <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                        Starting session...
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {url}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
            {/* Controls bar */}
            {!isTerminal && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  bgcolor: 'background.paper',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  startIcon={<StopIcon />}
                  onClick={onEndSession}
                >
                  End Session
                </Button>
              </Box>
            )}
          </Box>
        )}

        {activeTab === 1 && (
          /* Dashboard — stats & step table */
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <DashboardView
              steps={steps}
              stepResults={stepResults}
              phase={phase}
              duration={duration}
              summary={summary}
              qualityAssessment={qualityAssessment}
              currentPageIndex={currentPageIndex}
              totalPages={totalPages}
              currentPageTitle={currentPageTitle}
              pageResults={pageResults}
              sessionHistory={sessionHistory}
            />
          </Box>
        )}

        {activeTab === 2 && (
          /* Code Repository — the committed suite, not just this run's output */
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', p: 1 }}>
            <CodeRepositoryTab
              projectId={projectId}
              url={url}
              changedPaths={(generatedCode?.files || []).map((f: any) => f.path)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UITestingPanel;
